/**************************************************************************************************/
/** @import .                                                                                    **/
/**************************************************************************************************/
import Config from '@/config'

/**************************************************************************************************/
/** @import Classes/Records                                                                      **/
/**************************************************************************************************/
import FieldsOptions from '@/Classes/Records/FieldsOptions'
import { ItemMissingFields } from '@/Classes/Records/ItemMissingFields'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { AppValues } from '@/Constants/AppValues'
import { Documents } from '@/Constants/Documents'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { BuilderManager } from '@/Helpers/Components/DataTable/BuilderManager'
import { BMTypes } from '@/Helpers/Components/DataTable/BMTypes'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @export Namespace @name DataParsers                                                          **/
/**************************************************************************************************/
export namespace DataParsers
{
	/*************************************************/
	/** @Classes                                    **/
	/*************************************************/
	export class Companies
	{
		/*************************************************/
		/** @Methods                                    **/
		/*************************************************/
		static GetSummaryParser(stackedOrDefault: boolean) : (data: Array<any>) => any
		{
			if (stackedOrDefault)
			{
				return (data: Array<any>) =>
				{
					// Clase Constructora.
					const bm = new BuilderManager(data)

					// Eliminar los campos que no queremos mostrar en la tabla.
					bm.removeFields('_idAdminCompany', '_idCompany', 'address', 'code', 'commune', 'isClient', 'isValid', 'region', 'type')

					// Aplicar opciones a los campos.
					bm.parseFieldsOptions(new FieldsOptions()
						.add('rut', 'Rut').isSortable().showDetailsButton()
						.add('name', 'Razón Social').isSortable()
					)

					// Definir los formatos que serán aplicados al valor de la celda.
					bm.parseItemsFormatters({
						rut: (v: string) =>
						{
							return v === '' ? AppValues.Strings.DEFAULT_EMPTY_STRING : TypesHelper.toStringValidRutFormat(v)
						}
					})

					// Definir los campos a mostrar en el Detalle.
					bm.parseItemsDetail({
						['Dirección']: ({ address }) =>
						{
							return address
						},
						['Comuna']: ({ commune }) =>
						{
							return commune
						},
						['Región']: ({ region }) =>
						{
							return region
						},
						['Fono']: ({ phone }) =>
						{
							return TypesHelper.isArrayInvalidOrEmpty(phone) ? AppValues.Strings.DEFAULT_EMPTY_STRING : phone
						}
					})

					// Retornar Objeto con las propiedades.
					return bm.pack()
				}
			}
			else
			{
				return (data: Array<any>) =>
				{
					// Clase Constructora.
					const bm = new BuilderManager(data)

					// Eliminar los campos que no queremos mostrar en la tabla.
					bm.removeFields('_idAdminCompany', '_idCompany', 'code', 'isClient', 'isValid', 'storages', 'type')

					// Aplicar opciones a los campos.
					bm.parseFieldsOptions(new FieldsOptions()
						.add('rut', 'Rut').isSortable()
						.add('name', 'Razón Social').isSortable()
						.add('address', 'Dirección').isSortable()
						.add('commune', 'Comuna').isSortable()
						.add('region', 'Región').isSortable()
						.add('phone', 'Fono').isSortable()
					)

					// Aplicar un valor a los campos e items si no estan presentes.
					bm.parseItemsMissingFields(new ItemMissingFields()
						.add('address')
						.add('commune')
						.add('region')
						.add('phone')
					)

					// Definir los formatos que serán aplicados al valor de la celda.
					bm.parseItemsFormatters({
						rut: (v: string) =>
						{
							return v === '' ? AppValues.Strings.DEFAULT_EMPTY_STRING : TypesHelper.toStringValidRutFormat(v)
						},

						region: (v: string, item: any) =>
						{
							item.$region = v
							return TypesHelper.toStringLimitedLength(v.replace(/Región/g, 'R.'), 132)
						}
					})

					// Retornar Objeto con las propiedades.
					return bm.pack()
				}
			}
		}
	}

	export class Equipments
	{
		/*************************************************/
		/** @Methods                                    **/
		/*************************************************/
		static SummaryParser(data: Array<any>) : any
		{
			// Clase Constructora.
			const bm = new BuilderManager(data)

			// Eliminar los campos que no queremos mostrar en la tabla.
			bm.removeFields(
				'_idCompany', '_idStorage', '_idEquipment', 'hourmeter', 'company', 'status', 'group', 'serie', 'year', 'storageName', 'isValid'
			)

			// Aplicar opciones a los campos.
			bm.parseFieldsOptions(new FieldsOptions()
				.add('code', 'Código').isSortable()
				.add('name', 'Descripción').isSortable()
				.add('mark', 'Marca').isSortable()
				.add('typeArticle', 'Tipo Artículo').isSortable()
				.add('groupArticle', 'Grupo Artículo').isSortable()
				.add('typeEquipment', 'Tipo Equipo').isSortable()
				.add('groupEquipment', 'Grupo Equipo').isSortable()
				.add('companyName', 'Compañia').isSortable()
			)

			// Aplicar un reordenamiento a los campos.
			bm.rearrangeFields('code', 'name', 'serie', 'mark', 'typeEquipment', 'typeArticle', 'groupArticle', 'groupEquipment', 'year')

			// Aplicar un valor a los campos e items si no estan presentes.
			bm.parseItemsMissingFields(new ItemMissingFields()
				.add('mark')
				.add('serie')
				.add('year')
				.add('groupEquipment')
				.add('typeArticle')
			)

			// Retornar Objeto con las propiedades.
			return bm.pack()
		}
	}

	export class Storages
	{
		/*************************************************/
		/** @Methods                                    **/
		/*************************************************/
		static GetSummaryParser(stackedOrDefault: boolean) : (data: Array<any>, _actions?: any) => any
		{
			if (stackedOrDefault)
			{
				return (data: Array<any>, _actions?: any) =>
				{
					// Clase Constructora.
					const bm = new BuilderManager(data)

					// Mostrar unicamemte los campos que queremos en la tabla.
					bm.showFields('code', 'name')

					// Aplicar opciones a los campos.
					bm.parseFieldsOptions(new FieldsOptions()
						.add('code', 'Código').isSortable().setAlign(null, 'center').showDetailsButton()
						.add('name', 'Nombre').isSortable()
					)

					// Aplicar un nuevo Orden a todas las columnas visibles.
					bm.rearrangeFields('code', 'name')

					// Definir los campos a mostrar en el Detalle.
					const formatters: BMTypes.ItemDetailsDictionary =
					{
						['Código Planta']: ({ codeLocation }) =>
						{
							return codeLocation
						},
						['Dirección']: ({ address }) =>
						{
							return address
						},
						['Cliente']: ({ _idCompany }) =>
						{
							const company = Store.getters.getStoredCompanyById(_idCompany)
							return company ? company.name : AppValues.Strings.DEFAULT_EMPTY_STRING
						},
						['Técnico']: ({ _idTechnical }) =>
						{
							const user = Store.getters.getStoredUserById(_idTechnical)
							return user ? `${ user.name } ${ user.pLastName }` : AppValues.Strings.DEFAULT_EMPTY_STRING
						},
						['Adm. Contrato']: ({ _idUserAdmin }) =>
						{
							const user = Store.getters.getStoredUserById(_idUserAdmin)
							return user ? `${ user.name } ${ user.pLastName }` : AppValues.Strings.DEFAULT_EMPTY_STRING
						}
					}

					if (_actions !== undefined) formatters['Acciones'] = _actions
					bm.parseItemsDetail(formatters)

					// Retornar Objeto con las propiedades.
					return bm.packWithActions()
				}
			}
			else
			{
				return (data: Array<any>, _actions?: any) =>
				{
					// Clase Constructora.
					const bm = new BuilderManager(data, _actions)

					// Eliminar los campos que no queremos mostrar en la tabla.
					bm.removeFields('_id', '_idCompany', '_idStorage', '_idTechnical', '_idUserAdmin', 'codeAlmacenSap', 'codeLocation', 'contacts', 'equipments', 'commune', 'region')

					// Aplicar opciones a los campos.
					bm.parseFieldsOptions(new FieldsOptions()
						.add('code', 'Código').isSortable().setAlign(null, 'center')
						.add('name', 'Nombre').isSortable()
						.add('accessTotem', 'Acceso Totem').isSortable().setAlign(null, 'center')
						.add('address', 'Dirección').isSortable()
						.add('companyName', 'Cliente').isSortable()
						.add('technicalName', 'Técnico').isSortable().setAlign(null, 'center')
						.add('userAdminName', 'Adm. Contrato').isSortable().setAlign(null, 'center')
						.add('isValid', '¿Esta Activa?').isSortable().setAlign(null, 'center')
					)

					// Aplicar un nuevo Orden a todas las columnas visibles.
					bm.rearrangeFields('code', 'codeLocation', 'name', 'address', 'companyName', 'technicalName', 'userAdminName', 'accessTotem', 'isValid')

					// Aplicar un valor a los campos e items si no estan presentes.
					bm.parseItemsMissingFields(new ItemMissingFields()
						.add('address')
						.add('companyName')
						.add('technicalName')
						.add('userAdminName')
					)

					// Definir los formatos que serán aplicados al valor de la celda.
					bm.parseItemsFormatters({
						accessTotem: (v: string) =>
						{
							return v ? 'Habilitado' : 'Restringido'
						},
						isValid: (v: boolean) =>
						{
							return v ? 'Sí' : 'No'
						}
					})

					// Retornar Objeto con las propiedades.
					return bm.packWithActions()
				}
			}
		}
	}

	export class Users
	{
		/*************************************************/
		/** @Methods                                    **/
		/*************************************************/
		static SummaryExternalsParser(data: Array<any>) : any
		{
			// Clase Constructora.
			const bm = new BuilderManager(data)

			// Eliminar los campos que no queremos mostrar en la tabla.
			bm.removeFields('_idUser', '_idCompany', '_idRole', '_idWorkArea', 'mLastName', 'pLastName', 'permissions', 'status', 'codeSAP', 'codeBranch', 'nameBranch', 'originSAP', 'systemValid')

			// Aplicar opciones a los campos.
			bm.parseFieldsOptions(new FieldsOptions()
				.add('rut', 'Rut').isSortable().setAlign(null, 'center')
				.add('name', 'Nombre').isSortable()
				.add('email', 'Correo').isSortable()
				.add('phone', 'Fono').isSortable()
				.add('storages', 'Ubicaciones').isSortable()
			)

			// Aplicar un nuevo Orden a todas las columnas visibles.
			bm.rearrangeFields('rut', 'name', 'email', 'phone')

			// Definir los formatos que serán aplicados al valor de la celda.
			bm.parseItemsFormatters({
				name: (_: void, item: any) =>
				{
					return `${ item.name } ${ item.pLastName }`
				},

				phone: (_: void, item: any) =>
				{
					return TypesHelper.isArrayInvalidOrEmpty(item.phone) ? AppValues.Strings.DEFAULT_EMPTY_STRING : item.phone
				},

				storages: (values: Array<ObjectId>) =>
				{
					return values?.length > 0 ? values.map<string>((_idStorage) =>
					{
						const storage = Store.getters.getStoredStorageById(_idStorage)
						return storage ? `${ storage.code } ${ storage.name }` : AppValues.Strings.DEFAULT_EMPTY_STRING
					}) : AppValues.Strings.DEFAULT_EMPTY_STRING
				}
			})

			// Retornar Objeto con las propiedades.
			return bm.pack()
		}

		static GetSummaryInternalsParser(stackedOrDefault: boolean) : (data: Array<any>) => any
		{
			if (stackedOrDefault)
			{
				return (data: Array<any>) =>
				{
					// Clase Constructora.
					const bm = new BuilderManager(data)

					// Mostrar unicamemte los campos que queremos en la tabla.
					bm.showFields('rut', 'name', 'roleName', 'workAreaName', 'codeSAP', 'codeBranch', 'nameBranch', 'originSAP', 'systemValid')

					// Aplicar opciones a los campos.
					bm.parseFieldsOptions(new FieldsOptions()
						.add('rut', 'Rut').isSortable().setAlign(null, 'center').showDetailsButton()
						.add('name', 'Nombre').isSortable()
					)

					// Aplicar un nuevo Orden a todas las columnas visibles.
					bm.rearrangeFields('rut', 'name')

					// Eliminar los items que no queremos mostrar en la tabla.
					bm.removeItems(
						{ if: Config.entries.removeRootUser, field: '_idRole', value: Documents.Roles.Kreis.ROOT },
						{ field: '_idRole', value: Documents.Roles.Kreis.CLIENT }
					)

					// Definir los formatos que serán aplicados al valor de la celda.
					bm.parseItemsFormatters({
						name: (v: string, item: any) =>
						{
							return `${ v } ${ item.pLastName }`
						}
					})

					// Definir los campos a mostrar en el Detalle.
					bm.parseItemsDetail({
						['Correo']: ({ email }) =>
						{
							return email
						},
						['Fono']: ({ phone }) =>
						{
							return TypesHelper.isArrayInvalidOrEmpty(phone) ? AppValues.Strings.DEFAULT_EMPTY_STRING : phone
						},
						['Rol']: ({ _idRole }) =>
						{
							const role = Store.getters.getStoredRoleById(_idRole)
							return role ? role.name : AppValues.Strings.DEFAULT_EMPTY_STRING
						},
						['Area Trabajo']: ({ _idWorkArea }) =>
						{
							const workarea = Store.getters.getStoredWorkAreaById(_idWorkArea)
							return workarea ? workarea.name : AppValues.Strings.DEFAULT_EMPTY_STRING
						}
					})

					// Retornar Objeto con las propiedades.
					return bm.pack()
				}
			}
			else
			{
				return (data: Array<any>) =>
				{
					// Clase Constructora.
					const bm = new BuilderManager(data)

					// Eliminar los campos que no queremos mostrar en la tabla.
					bm.removeFields('_idUser', '_idCompany', '_idStorage', '_idRole', '_idWorkArea', 'isValid', 'pLastName', 'mLastName', 'companyName', 'storages', 'permissions', 'roleName', 'workAreaName', 'codeSAP', 'codeBranch', 'nameBranch', 'originSAP', 'systemValid')

					// Aplicar opciones a los campos.
					bm.parseFieldsOptions(new FieldsOptions()
						.add('rut', 'Rut').isSortable().setAlign(null, 'center')
						.add('name', 'Nombre').isSortable()
						.add('email', 'Correo').isSortable()
						.add('phone', 'Fono')
						.add('role', 'Rol').isSortable()
						.add('workarea', 'Área').isSortable()
					)

					// Aplicar un nuevo Orden a todas las columnas visibles.
					bm.rearrangeFields('rut', 'name', 'email', 'phone', 'role', 'workarea')

					// Aplicar un valor a los campos e items si no estan presentes.
					bm.parseItemsMissingFields(new ItemMissingFields()
						.add('role')
						.add('workarea')
					)

					// Eliminar los items que no queremos mostrar en la tabla.
					bm.removeItems(
						{ if: Config.entries.removeRootUser, field: '_idRole', value: Documents.Roles.Kreis.ROOT },
						{ field: '_idRole', value: Documents.Roles.Kreis.CLIENT }
					)

					// Definir los formatos que serán aplicados al valor de la celda.
					bm.parseItemsFormatters({
						name: (v: string, item: any) =>
						{
							return `${ v } ${ item.pLastName }`
						},

						role: (_: void, item: any) =>
						{
							const { _idRole } = item
							const role = Store.getters.getStoredRoleById(_idRole)
							return role ? role.name : AppValues.Strings.DEFAULT_EMPTY_STRING
						},

						phone: (_: void, item: any) =>
						{
							return TypesHelper.isArrayInvalidOrEmpty(item.phone) ? AppValues.Strings.DEFAULT_EMPTY_STRING : item.phone
						},

						workarea: (_: void, item: any) =>
						{
							const { _idWorkArea } = item
							const workarea = Store.getters.getStoredWorkAreaById(_idWorkArea)
							return workarea ? workarea.name : AppValues.Strings.DEFAULT_EMPTY_STRING
						}
					})

					// Retornar Objeto con las propiedades.
					return bm.pack()
				}
			}
		}
	}

	export class WorkAreas
	{
		/*************************************************/
		/** @Methods                                    **/
		/*************************************************/
		static SummaryParser(data: Array<any>) : any
		{
			// Clase Constructora.
			const bm = new BuilderManager(data)

			// Eliminar los campos que no queremos mostrar en la tabla.
			bm.removeFields('_idWorkArea', '_idLeader', 'status')

			// Aplicar opciones a los campos.
			bm.parseFieldsOptions(new FieldsOptions()
				.add('name', 'Nombre').isSortable()
				.add('leader', 'Líder').isSortable()
			)

			// Aplicar un nuevo Orden a todas las columnas visibles.
			bm.rearrangeFields('name', 'leader')

			// Aplicar un valor a los campos e items si no estan presentes.
			bm.parseItemsMissingFields(new ItemMissingFields()
				.add('leader')
			)

			// Definir los formatos que serán aplicados al valor de la celda.
			bm.parseItemsFormatters({
				leader: (_: void, item: any) =>
				{
					const { _idLeader } = item
					const user = Store.getters.getStoredUserById(_idLeader)
					return user ? `${ user.name } ${ user.pLastName }` : AppValues.Strings.DEFAULT_EMPTY_STRING
				}
			})

			// Retornar Objeto con las propiedades.
			return bm.pack()
		}
	}
}